import React, { useState, useEffect } from 'react';
import { Building, Copy, RefreshCw, Loader2, Check, Key, Users, MapPin, Share2 } from 'lucide-react';
import { api } from '../../services/api';
import AdminLayout from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import ShareApp from '../../components/ShareApp';

export default function AdminOrganization() {
  const [orgInfo, setOrgInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const loadOrgInfo = async () => {
    try {
      const data = await api.getOrganizationInfo();
      setOrgInfo(data);
    } catch (error) {
      console.error('Error loading org info:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrgInfo();
  }, []);

  const handleCopyCode = () => {
    if (orgInfo?.invite_code) {
      navigator.clipboard.writeText(orgInfo.invite_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleRegenerateCode = async () => {
    if (!window.confirm('Generate a new invite code? The old code will stop working.')) return;
    setRegenerating(true);
    try {
      const result = await api.regenerateInviteCode();
      setOrgInfo(prev => ({ ...prev, invite_code: result.invite_code }));
    } catch (error) {
      alert(error.message || 'Failed to regenerate code');
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-700" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-3xl" data-testid="admin-organization-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Organization Settings</h1>
          <p className="text-slate-500">Manage your organization and invite code</p>
        </div>

        {/* Org Info Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                <Building className="w-6 h-6 text-blue-700" />
              </div>
              <div>
                <CardTitle>{orgInfo?.name || 'Organization'}</CardTitle>
                <CardDescription>Plan: {orgInfo?.plan || 'Free'}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <Users className="w-5 h-5 mx-auto text-blue-700 mb-1" />
                <p className="text-2xl font-bold text-slate-800">{orgInfo?.guards_count || 0}</p>
                <p className="text-sm text-slate-500">Guards</p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <MapPin className="w-5 h-5 mx-auto text-green-700 mb-1" />
                <p className="text-2xl font-bold text-slate-800">{orgInfo?.sites_count || 0}</p>
                <p className="text-sm text-slate-500">Sites</p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <Key className="w-5 h-5 mx-auto text-amber-600 mb-1" />
                <p className="text-2xl font-bold text-slate-800">{orgInfo?.checkpoints_count || 0}</p>
                <p className="text-sm text-slate-500">Checkpoints</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Invite Code Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-600" />
              Guard Invite Code
            </CardTitle>
            <CardDescription>
              Share this code with guards to let them join your organization
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex-1 p-4 bg-slate-100 rounded-lg text-center">
                <p className="text-3xl font-mono font-bold text-slate-800 tracking-wider">
                  {orgInfo?.invite_code || 'N/A'}
                </p>
              </div>
              <Button onClick={handleCopyCode} variant="outline" className="gap-2">
                {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </div>
            
            <Button onClick={handleRegenerateCode} variant="outline" className="w-full gap-2" disabled={regenerating}>
              {regenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Regenerate Code
            </Button>

            {/* Share Button */}
            <Button onClick={() => setShowShareModal(true)} className="w-full bg-blue-700 hover:bg-blue-800 gap-2">
              <Share2 className="w-4 h-4" />
              Share App & Invite Guards
            </Button>

            <div className="bg-amber-50 p-4 rounded-lg">
              <p className="text-sm text-amber-800 font-medium mb-2">How Self-Signup Works:</p>
              <ol className="text-sm text-amber-700 list-decimal list-inside space-y-1">
                <li>Share the invite code with your guards</li>
                <li>Guards register at the app using the code</li>
                <li>You'll see their requests in "Join Requests"</li>
                <li>Approve or reject each request</li>
              </ol>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Share Modal */}
      <ShareApp
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        inviteCode={orgInfo?.invite_code}
        organizationName={orgInfo?.name}
      />
    </AdminLayout>
  );
}